import React from 'react';
import _ from 'underscore';

import {DataModificationsHeader} from 'audit/DataModifications';
import {callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {OPTIONS, RAW_TEXT_SPREADSHEET_NAME} from 'bulk-import/SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  Grid,
  ParentStorableData,
  PLATE_MAP_RAW_TEXT_NAME,
  SelectedParentStorable,
  UPLOAD_CONTAINERS_SPREADSHEET_FORMAT,
} from 'constants/SampleConstants';
import notifications from 'notifications';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import * as GridDAO from 'samples/GridDAO';
import {filterChangesToUpdatesOnly} from 'samples/grids/upload-containers/parseChanges';
import {Change} from 'samples/grids/upload-containers/UploadContainersFlowConstants';
import {invalidateGrid} from 'samples/StorableActions';
import {getParentStorableDatas} from 'samples/storables/ParentStorableUtil';
import * as ImportUtil from 'util/ImportUtil';
import {textToBlob} from 'util/TextToBlobUtil';

export enum UploadContainersActionType {
  OPEN_UPLOAD_CONTAINERS_FLOW = 'OPEN_UPLOAD_CONTAINERS_FLOW',
  CLOSE_UPLOAD_CONTAINERS_FLOW = 'CLOSE_UPLOAD_CONTAINERS_FLOW',
  UPLOAD_CONTAINERS_CHANGE_TEXT = 'UPLOAD_CONTAINERS_CHANGE_TEXT',
  UPLOAD_CONTAINERS_CHANGE_CONTAINER_SCHEMA = 'UPLOAD_CONTAINERS_CHANGE_CONTAINER_SCHEMA',
  UPLOAD_CONTAINERS_CHANGE_CONTAINER_SOURCE = 'UPLOAD_CONTAINERS_CHANGE_CONTAINER_SOURCE',
  UPLOAD_CONTAINERS_FLOW_GO_TO_NEXT = 'UPLOAD_CONTAINERS_FLOW_GO_TO_NEXT',
  UPLOAD_CONTAINERS_FLOW_GO_TO_PREV = 'UPLOAD_CONTAINERS_FLOW_GO_TO_PREV',
  VALIDATE_UPLOAD_CONTAINERS_STARTED = 'VALIDATE_UPLOAD_CONTAINERS_STARTED',
  VALIDATE_UPLOAD_CONTAINERS_COMPLETED = 'VALIDATE_UPLOAD_CONTAINERS_COMPLETED',
  VALIDATE_UPLOAD_CONTAINERS_FAILED = 'VALIDATE_UPLOAD_CONTAINERS_FAILED',
  UPLOAD_CONTAINERS_STARTED = 'UPLOAD_CONTAINERS_STARTED',
  UPLOAD_CONTAINERS_COMPLETED = 'UPLOAD_CONTAINERS_COMPLETED',
  UPLOAD_CONTAINERS_FAILED = 'UPLOAD_CONTAINERS_FAILED',
  UPLOAD_CONTAINERS_SELECT_PARENT = 'UPLOAD_CONTAINERS_SELECT_PARENT',
  UPLOAD_CONTAINERS_REMOVE_PARENT = 'UPLOAD_CONTAINERS_REMOVE_PARENT',
}

export interface OpenUploadContainersFlowAction {
  type: UploadContainersActionType.OPEN_UPLOAD_CONTAINERS_FLOW;
}

export function openUploadContainersFlow(): OpenUploadContainersFlowAction {
  return {
    type: UploadContainersActionType.OPEN_UPLOAD_CONTAINERS_FLOW,
  };
}

export interface CloseUploadContainersFlowAction {
  type: UploadContainersActionType.CLOSE_UPLOAD_CONTAINERS_FLOW;
}

export function closeUploadContainersFlow(): CloseUploadContainersFlowAction {
  return {
    type: UploadContainersActionType.CLOSE_UPLOAD_CONTAINERS_FLOW,
  };
}

export interface UploadContainersChangeTextAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_TEXT;
  rawText: string;
}

export function changeText(rawText: string): UploadContainersChangeTextAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_TEXT,
    rawText,
  };
}

export interface UploadContainersChangeContainerSchemaAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_CONTAINER_SCHEMA;
  selectedContainerSchemaId: string | null;
}

export function changeContainerSchema(
  selectedContainerSchemaId: string | null
): UploadContainersChangeContainerSchemaAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_CONTAINER_SCHEMA,
    selectedContainerSchemaId,
  };
}

export interface UploadContainersChangeContainerSourceAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_CONTAINER_SOURCE;
  selectedContainerSourceId: string;
}

export function changeContainerSource(
  selectedContainerSourceId: string
): UploadContainersChangeContainerSourceAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_CHANGE_CONTAINER_SOURCE,
    selectedContainerSourceId,
  };
}

interface UploadContainersFlowGoToNextAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_FLOW_GO_TO_NEXT;
}

export function goToNext(): UploadContainersFlowGoToNextAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_FLOW_GO_TO_NEXT,
  };
}

interface UploadContainersFlowGoToPrevAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_FLOW_GO_TO_PREV;
}

export function goToPrev(): UploadContainersFlowGoToPrevAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_FLOW_GO_TO_PREV,
  };
}

interface ValidateUploadContainersStartedAction {
  type: UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_STARTED;
}

export function validateUploadContainersStarted(): ValidateUploadContainersStartedAction {
  return {
    type: UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_STARTED,
  };
}

interface ValidateUploadContainersCompletedAction {
  type: UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_COMPLETED;
  gridPreview: Grid;
  changes: Array<Change>;
  spreadsheetUrl: string;
}

export function validateUploadContainersCompleted(
  changes: Array<Change>,
  gridPreview: Grid,
  spreadsheetUrl: string
): ValidateUploadContainersCompletedAction {
  return {
    type: UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_COMPLETED,
    changes,
    gridPreview,
    spreadsheetUrl,
  };
}

interface ValidateUploadContainersFailedAction {
  type: UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_FAILED;
  inputTextError: React.ReactNode;
  schemaError: string | null | undefined;
  projectError: string | undefined;
}

export function validateUploadContainersFailed({
  inputTextError,
  schemaError,
  projectError,
}: {
  inputTextError?: React.ReactNode;
  schemaError?: string | null;
  projectError?: string;
}): ValidateUploadContainersFailedAction {
  return {
    type: UploadContainersActionType.VALIDATE_UPLOAD_CONTAINERS_FAILED,
    inputTextError,
    schemaError,
    projectError,
  };
}

export function validateUploadFromPlateMap(gridId: string, registryId: string): Thunk<void> {
  return async (dispatch: BenchlingDispatch, getState: () => BenchlingState) => {
    const {rawText, selectedContainerSchemaId, selectedContainerSourceId} =
      getState().ui.samples.uploadContainersFlow;
    const inputTextError = !rawText ? 'Please enter text.' : null;
    const schemaError = !selectedContainerSchemaId ? 'Please select a container schema.' : null;
    const projectError =
      BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES &&
      selectedContainerSourceId === registryId
        ? 'Please select a project.'
        : undefined;
    if (inputTextError || schemaError || projectError) {
      dispatch(validateUploadContainersFailed({inputTextError, schemaError, projectError}));
    } else {
      const spreadsheetBlob: Blob & {name?: string} = textToBlob(rawText);
      spreadsheetBlob.name = RAW_TEXT_SPREADSHEET_NAME;
      const file = new File([spreadsheetBlob], spreadsheetBlob.name, {
        type: spreadsheetBlob.type,
      });
      dispatch(validateUploadContainersStarted());
      try {
        const url = (await ImportUtil.upload(file))!;
        const {changes, errors, gridPreview} = (await GridDAO.validateUploadContainersFromSpreadsheet(
          gridId,
          selectedContainerSchemaId!,
          selectedContainerSourceId!,
          PLATE_MAP_RAW_TEXT_NAME,
          url,
          UPLOAD_CONTAINERS_SPREADSHEET_FORMAT.PLATE_MAP
        ))!;

        const errorMessagesByRow = SpreadsheetBulkImportUtil.getErrorMessages(
          errors,
          // Don't block on the following errors
          {
            [OPTIONS.USE_EXISTING_CONTAINERS]: true,
            [OPTIONS.USE_NON_EMPTY_CONTAINERS]: true,
            [OPTIONS.USE_SAMPLES_WITH_STATUS_WARNINGS]: true,
          }
        );
        const errorMessages = _([...errorMessagesByRow.values()])
          .flatten()
          .filter((error) => error.icon === 'error')
          .filter((error) => !error.resolved)
          .map((error) => error.message);

        if (errorMessages.length > 0) {
          dispatch(
            validateUploadContainersFailed({
              inputTextError: errorMessages.map((errorMessage) => <div>{errorMessage}</div>),
            })
          );
        } else {
          dispatch(validateUploadContainersCompleted(changes, gridPreview, url));
          dispatch(goToNext());
        }
      } catch (e) {
        const errorMessage = notifications.getFriendlyError(e);
        if (errorMessage) {
          dispatch(validateUploadContainersFailed({inputTextError: errorMessage}));
        } else {
          dispatch(validateUploadContainersFailed({}));
          throw e;
        }
      }
    }
  };
}

interface UploadContainersStartedAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_STARTED;
}

export function uploadContainersStarted(): UploadContainersStartedAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_STARTED,
  };
}

interface UploadContainersCompletedAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_COMPLETED;
}

export function uploadContainersCompleted(): UploadContainersCompletedAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_COMPLETED,
  };
}

interface UploadContainersFailedAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_FAILED;
}

export function uploadContainersFailed(): UploadContainersFailedAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_FAILED,
  };
}

function uploadContainersFromSpreadsheetWithDataMods(
  dispatch: BenchlingDispatch,
  gridId: string,
  selectedContainerSchemaId: string,
  selectedContainerSourceId: string,
  spreadsheetUrl: string,
  parentStorableDatas: Array<ParentStorableData>,
  modifiedContainerIds: Array<string>
): void {
  const changeCount = modifiedContainerIds.length;

  callWithDataModifications({
    changeSummary:
      changeCount > 0
        ? getInventoryRegistryChangeSummary({
            apiIds: [],
            itemCounts: {container: changeCount},
            action: InventoryRegistryDataModAction.MOVE,
          })
        : // Data mods modal will not triggered if there are no changes
          '',
    onPerformAction: (dataModsHeader?) => {
      uploadContainersFromSpreadsheet(
        dispatch,
        gridId,
        selectedContainerSchemaId,
        selectedContainerSourceId,
        spreadsheetUrl,
        parentStorableDatas,
        dataModsHeader
      );
    },
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MOVE,
      numModifiedTotalItems: changeCount,
      entryId: null,
    },
    apiIds: modifiedContainerIds,
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY && changeCount > 0,
  });
}

function uploadContainersFromSpreadsheet(
  dispatch: BenchlingDispatch,
  gridId: string,
  selectedContainerSchemaId: string,
  selectedContainerSourceId: string,
  spreadsheetUrl: string,
  parentStorableDatas: Array<ParentStorableData>,
  dataModsHeader?: DataModificationsHeader
): void {
  dispatch(uploadContainersStarted());
  GridDAO.uploadContainersFromSpreadsheet(
    gridId,
    selectedContainerSchemaId,
    selectedContainerSourceId,
    PLATE_MAP_RAW_TEXT_NAME,
    spreadsheetUrl,
    UPLOAD_CONTAINERS_SPREADSHEET_FORMAT.PLATE_MAP,
    parentStorableDatas,
    dataModsHeader
  )
    .then(() => {
      notifications.success('Successfully set containers');
      invalidateGrid(gridId);
      dispatch(uploadContainersCompleted());
    })
    .fail((e) => {
      notifications.notifyError(e);
      dispatch(uploadContainersFailed());
    });
}

export function uploadFromPlateMap(): Thunk<void> {
  return (dispatch: BenchlingDispatch, getState: () => BenchlingState) => {
    const {
      gridPreview,
      changeStats,
      selectedContainerSchemaId,
      selectedContainerSourceId,
      selectedParent,
      spreadsheetUrl,
      changes,
    } = getState().ui.samples.uploadContainersFlow;
    const gridId = gridPreview!.id;
    uploadContainersFromSpreadsheetWithDataMods(
      dispatch,
      gridId,
      selectedContainerSchemaId!,
      selectedContainerSourceId!,
      spreadsheetUrl!,
      getParentStorableDatas(changeStats!.moveOut, selectedParent),
      filterChangesToUpdatesOnly(changes ?? []).map(({container}: Change) => container.id)
    );
  };
}

interface UploadContainersSelectParentAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_SELECT_PARENT;
  selectedParent: SelectedParentStorable;
}

export function selectParent(selectedParent: SelectedParentStorable): UploadContainersSelectParentAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_SELECT_PARENT,
    selectedParent,
  };
}

interface UploadContainersRemoveParentAction {
  type: UploadContainersActionType.UPLOAD_CONTAINERS_REMOVE_PARENT;
}

export function removeParent(): UploadContainersRemoveParentAction {
  return {
    type: UploadContainersActionType.UPLOAD_CONTAINERS_REMOVE_PARENT,
  };
}

export type UploadContainersAction =
  | OpenUploadContainersFlowAction
  | CloseUploadContainersFlowAction
  | UploadContainersChangeTextAction
  | UploadContainersChangeContainerSchemaAction
  | UploadContainersChangeContainerSourceAction
  | UploadContainersFlowGoToNextAction
  | UploadContainersFlowGoToPrevAction
  | ValidateUploadContainersStartedAction
  | ValidateUploadContainersCompletedAction
  | ValidateUploadContainersFailedAction
  | UploadContainersStartedAction
  | UploadContainersCompletedAction
  | UploadContainersFailedAction
  | UploadContainersSelectParentAction
  | UploadContainersRemoveParentAction;
